import LegalLayout, { Section, List } from "@/pages/legal/LegalLayout";

export default function PrivacyPolicy() {
    return (
        <LegalLayout
            eyebrow="Privacy Policy"
            title="How we handle your information."
            lede="HomeIQ is an AI-assisted home-buying platform. We ask for a small amount of information to produce your affordability read, loan analysis, market intelligence, investment view, and Vastu report. This page explains what we collect, how we use it, and the choices you have."
            lastUpdated="1 July 2026"
        >
            <Section title="Information we collect">
                <p>We only collect what the product genuinely needs. That falls into four groups:</p>
                <List
                    items={[
                        <><strong>Financial inputs</strong> — monthly rent, salary, existing obligations, down-payment, loan tenure and interest rate you enter into the calculator. These live in your browser session unless you generate a HomeIQ Report.</>,
                        <><strong>Report lead details</strong> — when you generate a PDF report, we ask for your name, email address, and (optionally) phone number so we can label the report and send it to you.</>,
                        <><strong>Uploaded floor plans</strong> — if you use the Vastu Analyzer, the PDF, PNG or JPG you upload is used once, in-memory, to extract room directions. We do not persist the original file.</>,
                        <><strong>Product usage</strong> — standard web analytics (pages viewed, referring source, device class). We do not use fingerprinting or third-party ad trackers.</>,
                    ]}
                />
            </Section>

            <Section title="How we use it">
                <List
                    items={[
                        "To compute your affordability, loan, market and investment reads using our own Core calculation engines that run in your browser.",
                        "To send Vastu floor plans to our AI provider so it can identify the direction of key rooms, then return a scored advisory read to you.",
                        "To assemble your HomeIQ Report PDF and email it to the address you provided.",
                        "To track aggregate product usage so we know what to improve. We never sell or share individual profiles.",
                    ]}
                />
            </Section>

            <Section title="How we handle floor-plan uploads">
                <p>Floor plans deserve special care because they reveal the layout of your future home.</p>
                <List
                    items={[
                        "Uploads are limited to 8 MB and accepted only as PDF, PNG or JPG.",
                        "The file is rasterised in memory, downscaled to a canonical 1600-px canvas, and forwarded to our AI provider (see below).",
                        "We do not store the original file on our servers. The extracted room-direction JSON is persisted only when it forms part of a HomeIQ Report.",
                        "Password-protected PDFs are rejected — we cannot bypass encryption.",
                    ]}
                />
            </Section>

            <Section title="Third-party services">
                <p>To deliver the product, we send parts of your data to trusted providers. Each has been chosen for its posture on privacy and its role is strictly scoped.</p>
                <List
                    items={[
                        <><strong>Anthropic (Claude Opus)</strong> — receives your Vastu floor-plan image and returns extracted room directions. Governed by Anthropic's commercial terms and privacy policy.</>,
                        <><strong>MongoDB Atlas</strong> — stores generated HomeIQ Reports (report ID, lead name/email/phone, and the calculation snapshot) so you can download them again later.</>,
                        <><strong>Hosting / CDN</strong> — the site and API are served through a managed cloud environment; standard operational logs (IP, user-agent, timestamps) are retained for a short window for security.</>,
                        <><strong>Email delivery</strong> — when configured, transactional email (your report PDF) is sent through Resend. Only your name, email and the PDF attachment are transmitted.</>,
                    ]}
                />
            </Section>

            <Section title="Data security">
                <List
                    items={[
                        "All traffic to and from HomeIQ is served over HTTPS.",
                        "Report data is stored in a private database not exposed to the public internet.",
                        "We limit internal access to production data to the small team that operates HomeIQ.",
                        "You can request deletion of any report you generated by emailing the contact address below with the report ID.",
                    ]}
                />
            </Section>

            <Section title="Your choices">
                <List
                    items={[
                        "You can use the landing page and all calculators without providing any personal information.",
                        "The Vastu Analyzer requires an uploaded floor plan or a manual room description — beyond that, no personal identifier is required.",
                        "A HomeIQ Report requires a name and email so we can deliver it. Phone number is optional.",
                        "To ask us to delete your report or lead record, send us the report ID via the contact channel below.",
                    ]}
                />
            </Section>

            <Section title="Contact">
                <p>
                    For any privacy question — deletion requests, corrections, or clarification about how a specific piece of data is used — reach us on WhatsApp at <a href="https://wa.me/917406054477" target="_blank" rel="noopener noreferrer" className="text-[#1E6BFF] font-semibold hover:underline">+91 74060 54477</a>.
                </p>
            </Section>
        </LegalLayout>
    );
}
